import classNames from 'class-names';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import {
  AccordionContext,
  Accordion as BSAccordion,
  Card,
  Col,
  Row,
  useAccordionToggle,
} from 'react-bootstrap';

import Button from '../../Atoms/Button/Button';
import { Icon } from '../../Atoms/Icon';

import styles from './accordion.module.css';

const Arrow = ({ isOpen, arrowClass = '' }) => {
  return (
    <span>
      <Icon
        className={classNames(
          arrowClass,
          styles.transition,
          isOpen && styles.rotate90,
        )}
        name="chevron-right"
      />
    </span>
  );
};
Arrow.propTypes = {
  isOpen: PropTypes.bool,
  arrowClass: PropTypes.string,
};

const CustomToggle = ({
  eventKey,
  onExpand,
  children,
  controlBtnActiveClass = '',
  controlBtnClass = '',
  arrow = 'end',
  arrowClass = '',
}) => {
  const decoratedOnClick = useAccordionToggle(eventKey, () =>
    onExpand ? onExpand(eventKey) : '',
  );
  const currentEventKey = useContext(AccordionContext);
  const isCurrentEventKey = currentEventKey === eventKey;

  return (
    <Row className="mx-0">
      <Col className="px-0">
        <Button
          className={classNames(
            controlBtnClass,
            'btn-block control-btn',
            isCurrentEventKey ? controlBtnActiveClass : 'open',
          )}
          variant="link"
          onClick={decoratedOnClick}
          onKeyDown={decoratedOnClick}
          aria-hidden="true"
        >
          {arrow === 'start' && (
            <Arrow isOpen={isCurrentEventKey} arrowClass={arrowClass} />
          )}
          <div className="w-100 d-flex justify-content-between">
            <div
              className={classNames(
                'w-100',
                arrow === 'start' && 'ml-2',
                arrow === 'end' && 'mr-2',
              )}
            >
              {children}
            </div>
            {arrow === 'end' && (
              <Arrow isOpen={isCurrentEventKey} arrowClass={arrowClass} />
            )}
          </div>
        </Button>
      </Col>
    </Row>
  );
};
CustomToggle.propTypes = {
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onExpand: PropTypes.func,
  children: PropTypes.node.isRequired,
  controlBtnActiveClass: PropTypes.string,
  controlBtnClass: PropTypes.string,
  arrow: PropTypes.oneOf(['start', 'end', 'none']),
  arrowClass: PropTypes.string,
};

const Accordion = ({ children, defaultActiveKey = '0', onClick }) => {
  return (
    <BSAccordion defaultActiveKey={defaultActiveKey} onClick={onClick}>
      {children}
    </BSAccordion>
  );
};
Accordion.propTypes = {
  children: PropTypes.node.isRequired,
  defaultActiveKey: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const AccordionCard = ({ children, className = 'bg-transparent pb-2' }) => {
  return <Card className={className}>{children}</Card>;
};
AccordionCard.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

const AccordionCardHeader = ({
  controlBtnActiveClass = '',
  controlBtnClass = 'text-left text-gray-dark bg-gray-300',
  children,
  eventKey = '0',
  arrow = 'end',
  arrowClass = '',
}) => {
  return (
    <Card.Header className="bg-transparent p-0">
      <CustomToggle
        eventKey={eventKey}
        controlBtnClass={controlBtnClass}
        controlBtnActiveClass={controlBtnActiveClass}
        arrow={arrow}
        arrowClass={arrowClass}
      >
        {children}
      </CustomToggle>
    </Card.Header>
  );
};
AccordionCardHeader.propTypes = {
  controlBtnActiveClass: PropTypes.string,
  controlBtnClass: PropTypes.string,
  children: PropTypes.node.isRequired,
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  arrow: PropTypes.oneOf(['start', 'end', 'none']),
  arrowClass: PropTypes.string,
};

const AccordionCardCollapse = ({
  children,
  eventKey = '0',
  height,
  onChangeEventKey = null,
}) => {
  const currentEventKey = useContext(AccordionContext);
  let accordionHeight = 'auto';
  if (currentEventKey === eventKey && onChangeEventKey) {
    onChangeEventKey(currentEventKey);
  }
  if (height) {
    accordionHeight = currentEventKey === eventKey ? `${height}px` : 0;
  }
  return (
    <div
      style={{
        maxHeight: accordionHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <BSAccordion.Collapse eventKey={eventKey} style={{ height: 'auto' }}>
        <Card.Body className="p-0">{children}</Card.Body>
      </BSAccordion.Collapse>
    </div>
  );
};
AccordionCardCollapse.propTypes = {
  children: PropTypes.node.isRequired,
  eventKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  height: PropTypes.number,
  onChangeEventKey: PropTypes.func,
};

Accordion.Card = AccordionCard;
Accordion.CardHeader = AccordionCardHeader;
Accordion.CardCollapse = AccordionCardCollapse;

export default Accordion;
